import { useState } from 'react';
import { Loader2, Plus, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { PERMISSIONS } from '@virtuetasks/types';
import { PageHeader } from '@/app/layout/AppShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { UserAvatar } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { EmptyState } from '@/components/shared/EmptyState';
import { ApiError } from '@/lib/api';
import { useCan } from '@/features/auth';
import { useCreateTeam, useTeamMembers, useTeams, useUsers, type PersonView } from './api';

const TEAM_COLOURS = ['#2F6F4E', '#4A7CB5', '#C9762B', '#C2413A', '#7C5CB5', '#0E8A8A'];

function CreateTeamDialog({
  open,
  onOpenChange,
  people,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  people: PersonView[];
}) {
  const create = useCreateTeam();
  const [name, setName] = useState('');
  const [leadId, setLeadId] = useState('');
  const [colour, setColour] = useState(TEAM_COLOURS[0]);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setError(null);
    try {
      await create.mutateAsync({ name: name.trim(), colour, leadId, memberIds: [] });
      toast.success(`Team “${name}” created`);
      setName('');
      setLeadId('');
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create the team');
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle className="mb-4 text-[16px] font-semibold">New team</DialogTitle>
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="team-name">Name</Label>
            <Input id="team-name" autoFocus value={name} onChange={(e) => setName(e.target.value)} />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="team-lead">Lead</Label>
            <select
              id="team-lead"
              className="h-8 rounded-[6px] border border-[var(--border)] bg-[var(--surface)] px-2 text-[13px]"
              value={leadId}
              onChange={(e) => setLeadId(e.target.value)}
            >
              <option value="">Choose a lead…</option>
              {people
                .filter((p) => p.status !== 'suspended')
                .map((p) => (
                  <option key={p._id} value={p._id}>
                    {p.name}
                  </option>
                ))}
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Colour</Label>
            <div className="flex gap-1.5">
              {TEAM_COLOURS.map((c) => (
                <button
                  key={c}
                  type="button"
                  aria-label={`Colour ${c}`}
                  aria-pressed={colour === c}
                  onClick={() => setColour(c)}
                  className="size-6 rounded-full border-2 transition-transform duration-[120ms] hover:scale-110"
                  style={{ background: c, borderColor: colour === c ? 'var(--text)' : 'transparent' }}
                />
              ))}
            </div>
          </div>

          {error && (
            <p role="alert" className="rounded-[6px] bg-[var(--danger)]/10 px-2.5 py-2 text-[12px] text-[var(--danger)]">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={submit} disabled={!name || !leadId || create.isPending}>
              {create.isPending ? <Loader2 className="animate-spin" /> : <Plus />}
              Create team
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function TeamsPage() {
  const can = useCan();
  const { data: teams, isLoading } = useTeams();
  const { data: people = [] } = useUsers();
  const { add, remove, destroy } = useTeamMembers();
  const [createOpen, setCreateOpen] = useState(false);

  const byId = new Map(people.map((p) => [p._id, p]));
  const canEdit = can(PERMISSIONS.TEAM_UPDATE);

  return (
    <>
      <PageHeader
        title="Teams"
        subtitle="Teams give managers their scope and drive workload rollups."
        actions={
          can(PERMISSIONS.TEAM_CREATE) ? (
            <Button variant="primary" onClick={() => setCreateOpen(true)}>
              <Plus />
              New team
            </Button>
          ) : null
        }
      />

      <div className="flex flex-col gap-3 p-4">
        {isLoading ? (
          [0, 1].map((i) => <Skeleton key={i} className="h-28" />)
        ) : !teams || teams.length === 0 ? (
          <EmptyState
            title="No teams yet"
            description="Group people into teams so managers get a scope and workload rolls up."
            action={
              can(PERMISSIONS.TEAM_CREATE) ? (
                <Button variant="primary" onClick={() => setCreateOpen(true)}>
                  <Plus />
                  Create the first team
                </Button>
              ) : undefined
            }
          />
        ) : (
          teams.map((team) => {
            const members = team.memberIds.map((id) => byId.get(id)).filter(Boolean) as PersonView[];
            const available = people.filter(
              (p) => !team.memberIds.includes(p._id) && p.status !== 'suspended',
            );
            return (
              <section key={team._id} className="vt-surface p-3">
                <div className="mb-3 flex items-center gap-2">
                  <span className="size-2.5 rounded-full" style={{ background: team.colour }} aria-hidden />
                  <h2 className="text-[15px] font-semibold">{team.name}</h2>
                  <span className="text-[11px] vt-muted">
                    {members.length} {members.length === 1 ? 'member' : 'members'} · led by{' '}
                    {byId.get(team.leadId)?.name ?? 'unknown'}
                  </span>
                  {can(PERMISSIONS.TEAM_DELETE) && (
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      className="ml-auto text-[var(--danger)]"
                      aria-label={`Delete ${team.name}`}
                      onClick={() => {
                        void destroy
                          .mutateAsync(team._id)
                          .then(() => toast.success(`Team “${team.name}” deleted`))
                          .catch((err: unknown) =>
                            toast.error(err instanceof ApiError ? err.message : 'Could not delete'),
                          );
                      }}
                    >
                      <Trash2 />
                    </Button>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-1.5">
                  {members.map((m) => (
                    <span
                      key={m._id}
                      className="inline-flex items-center gap-1.5 rounded-full bg-[var(--surface-2)] py-0.5 pl-0.5 pr-1.5 text-[12px]"
                    >
                      <UserAvatar id={m._id} name={m.name} src={m.avatarUrl} size={20} />
                      {m.name}
                      {m._id === team.leadId && <span className="text-[10px] vt-muted">lead</span>}
                      {canEdit && m._id !== team.leadId && (
                        <button
                          type="button"
                          aria-label={`Remove ${m.name} from ${team.name}`}
                          className="ml-0.5 text-[var(--text-muted)] hover:text-[var(--danger)]"
                          onClick={() => {
                            void remove
                              .mutateAsync({ teamId: team._id, userIds: [m._id] })
                              .catch((err: unknown) =>
                                toast.error(err instanceof ApiError ? err.message : 'Could not remove'),
                              );
                          }}
                        >
                          <X className="size-3" />
                        </button>
                      )}
                    </span>
                  ))}

                  {canEdit && available.length > 0 && (
                    <select
                      className="h-7 rounded-[6px] border border-dashed border-[var(--border)] bg-transparent px-2 text-[12px] vt-muted"
                      value=""
                      aria-label={`Add someone to ${team.name}`}
                      onChange={(e) => {
                        if (!e.target.value) return;
                        void add
                          .mutateAsync({ teamId: team._id, userIds: [e.target.value] })
                          .catch((err: unknown) =>
                            toast.error(err instanceof ApiError ? err.message : 'Could not add'),
                          );
                      }}
                    >
                      <option value="">+ Add member</option>
                      {available.map((p) => (
                        <option key={p._id} value={p._id}>
                          {p.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </section>
            );
          })
        )}
      </div>

      <CreateTeamDialog open={createOpen} onOpenChange={setCreateOpen} people={people} />
    </>
  );
}
